import { FC, useState } from 'react'

import { useRouter } from 'next/navigation'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'

import { Box, TextField } from '@style-port/roov-ui-components-react'
import { ConversionButton, Txt } from 'src/components/atoms'

import { yup, BaseYup, yupResolver } from 'src/customYup'
import { useAuth, useProfileRequiredToBeUpdated } from 'src/hooks'
import { Routes } from 'src/routes'
import { ProfileEntity, UpdateInitialPasswordFormEntity } from 'src/types'

const Container = styled(Box)`
  background: rgba(255, 255, 255, 0.6);
  border-radius: 1.14rem;
`

const schema: yup.SchemaOf<UpdateInitialPasswordFormEntity> = BaseYup.object({
  name: BaseYup.string().required(),
  newPassword: BaseYup.string().required('入力して下さい'),
  newPasswordConfirm: BaseYup.string()
    .oneOf([BaseYup.ref('newPassword'), null], '新しいパスワードが一致しません')
    .required('入力して下さい'),
})

type Props = {
  profile: ProfileEntity
  className?: string
}

export const UpdateInitialPassword: FC<Props> = ({ profile, className }) => {
  const [isLoading, setIsLoading] = useState(false)

  const router = useRouter()
  const { updateInitialPassword } = useAuth()
  const { clearProfileRequiredToBeUpdated } = useProfileRequiredToBeUpdated()

  const { register, handleSubmit, watch } = useForm<UpdateInitialPasswordFormEntity>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: profile.name ?? '',
    },
  })

  const onSubmit = async (formEntity: UpdateInitialPasswordFormEntity) => {
    setIsLoading(true)

    try {
      await updateInitialPassword({ formEntity })
      clearProfileRequiredToBeUpdated()
      router.push(Routes.Dashboard.path)
    } catch (error) {
      console.log('e: ', error)
      // TODO: エラー時の対応
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <section className={className}>
      <Container padding="--r3">
        <div className="tw-flex tw-flex-col tw-items-center">
          <Txt className="tw-font-bold">{profile.name ? '本パスワード設定' : '登録'}</Txt>
          <div className="tw-w-full tw-mt-s1">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="tw-grid tw-gap-s0">
                {!profile.name && (
                  <TextField
                    placeholder="お名前"
                    interactive
                    autoComplete="off"
                    {...register('name')}
                    className="tw-w-full"
                  />
                )}
                {!profile.name && (
                  <TextField
                    placeholder="メールアドレス"
                    type="email"
                    interactive
                    disabled
                    defaultValue={profile.email}
                    className="tw-w-full"
                  />
                )}
                <TextField
                  placeholder="パスワード（英数字を含む8文字以上）"
                  type="password"
                  autoComplete="new-password"
                  interactive
                  {...register('newPassword')}
                  className="tw-w-full"
                />
                <TextField
                  placeholder="パスワードの再入力"
                  type="password"
                  interactive
                  {...register('newPasswordConfirm')}
                  className="tw-w-full"
                />
              </div>
              <div className="tw-centering tw-mt-s2">
                <ConversionButton
                  text={profile.name ? '本パスワード設定' : '登録'}
                  size="l"
                  isLoading={isLoading}
                  disabled={!watch('name') || !watch('newPassword') || !watch('newPasswordConfirm')}
                  prependIconPath={profile.name ? 'settings/soft/line' : 'check/soft/line'}
                  type="submit"
                />
              </div>
            </form>
          </div>
        </div>
      </Container>
    </section>
  )
}
